import * as React from 'react';
import Link from 'components/Link';
import firebase from 'firebase';
import { useRouter, type NextRouter } from 'next/router';
import { useApolloClient, ApolloClient } from '@apollo/client';
import useViewer from 'hooks/useViewer';
import useStripe from 'hooks/useStripe';
import Head from 'components/Head';
import { LOGIN_PAGE, PRICING_PAGE, TERMS_OF_SERVICE_PAGE } from 'constants/pages';
import { getPriceIdForRedirect, isValidPriceId } from 'constants/payments';
import { AuthStatus } from 'constants/auth';
import { auth } from 'services/client/firebase';
import api from 'services/client/api';
import Sentry from 'utils/shared/sentry';
import { RequestStatus, ErrorResponse } from 'constants/requests';
import { updateViewerToken, FirebaseUserOrNull } from 'services/client/token';
import { getIsNativePlatform } from 'utils/client/mobile';
import FormWrapper from './FormWrapper';
import { GET_AUTH_USER } from './graphql';

import {
  Input,
  Label,
  FieldWrapper,
  Button,
  InlineCheckbox,
  CheckboxInlineLabel,
  InlineCheckboxWrapperLabel,
} from 'styles/elements';
import { FormTitle, BottomLink, ErrorWrapper, TextLink } from './styles';
import Google from 'svg/Google';

const TITLE = 'Sign Up';
const DESCRIPTION = 'Create an InsiderFinance account';
const POLL_TIME_MS = 500;
const ONE_MINUTE = 60 * 1000;
const MAX_POLLS = ONE_MINUTE / POLL_TIME_MS;

interface CheckoutSessionArgs {
  product: string;
  term: string;
  user?: firebase.User | null;
  stripe?: stripe.Stripe;
  router: NextRouter;
}

type Resolve = (value: unknown) => void;
type Reject = (reason?: any) => void;

const pollForUser = (client: ApolloClient<object>, user: FirebaseUserOrNull) => {
  let requestMade = 0;
  let success = false;

  if (!user?.uid) {
    return Promise.reject(new Error('Did not have a valid user ID'));
  }

  const makeQuery = async (resolve: Resolve, reject: Reject) => {
    requestMade = requestMade + 1;

    if (requestMade === MAX_POLLS) {
      reject(new Error('User poll timed out'));
    }

    const result = await client.query({
      query: GET_AUTH_USER,
      variables: { id: user.uid },
      fetchPolicy: 'no-cache',
    });

    if (result.errors) {
      return reject(result.errors);
    } else if (result.data && result.data.users_by_pk) {
      success = true;
      return resolve(result.data.users_by_pk);
    } else if (!success) {
      setTimeout(makeQuery, POLL_TIME_MS, resolve, reject);
    }
  };

  return updateViewerToken(user, true).then((resp) => {
    if (!resp.token) {
      throw new Error('Could not find user token');
    }

    return new Promise(makeQuery);
  });
};

const maybeCreateCheckoutSession = ({
  product,
  term,
  user,
  stripe,
  router,
}: CheckoutSessionArgs) => {
  const priceId = isValidPriceId(product as string)
    ? product
    : getPriceIdForRedirect(product as string, term as string);

  let checkoutSessionPromise: Promise<any> = Promise.resolve();

  if (priceId && stripe) {
    checkoutSessionPromise = !user
      ? Promise.resolve()
      : user.getIdToken().then((idToken) =>
          api
            .post('payments/stripe/checkout', {
              payload: {
                token: idToken,
                stripePriceId: priceId,
              },
            })
            .then(function (session) {
              if (session && session.statusCode && session.statusCode > 201 && session.message) {
                throw new Error(session.message);
                return;
              }

              if (!session || !session.id) {
                throw new Error('Could not create subscription. Refresh the page and try again.');
                return;
              }

              // NOTE: This was not recognizing the custom domain
              // await stripe.redirectToCheckout({
              //   sessionId: session.id,
              // });

              window.location.href = session.url;
            }),
        );
  } else {
    const next = router.query.next as string;
    const redirectUrl = next || PRICING_PAGE;
    window.location.href = redirectUrl;
  }

  return checkoutSessionPromise;
};

const Signup = () => {
  const router = useRouter();
  const stripe = useStripe({});
  const viewer = useViewer();
  const client = useApolloClient();
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [isCreatingSession, setIsCreatingSession] = React.useState(false);
  const [error, setError] = React.useState<ErrorResponse | null>(null);
  const [requestStatus, setRequestStatus] = React.useState(RequestStatus.Idle);
  const isDisabled =
    requestStatus === RequestStatus.InProgress ||
    requestStatus === RequestStatus.Success ||
    viewer.status === AuthStatus.User;
  const isHidden =
    (viewer.status === AuthStatus.Loading || viewer.status === AuthStatus.User) &&
    !error &&
    requestStatus !== RequestStatus.InProgress &&
    requestStatus !== RequestStatus.Success;
  const { product, term } = router.query;
  let pathname = '';
  if (product) {
    if (term) {
      pathname = `/${product}/${term}`;
    } else {
      pathname = `/${product}`;
    }
  }
  const [termsAccepted, setTermsAccepted] = React.useState(false);

  // NOTE: Not letting them sign up on mobile currently
  React.useEffect(() => {
    if (router.isReady) {
      if (getIsNativePlatform()) {
        router.push(LOGIN_PAGE);
      }
    }
  }, [router.isReady]);

  React.useEffect(() => {
    const isIdle =
      requestStatus !== RequestStatus.InProgress &&
      requestStatus !== RequestStatus.Success &&
      !isCreatingSession;
    const hasViewer = viewer.status === AuthStatus.User && viewer.viewer;

    if (hasViewer && product && stripe && isIdle) {
      setIsCreatingSession(true);
      maybeCreateCheckoutSession({
        product: product as string,
        term: term as string,
        stripe,
        user: viewer.viewer,
        router,
      });
    } else if (hasViewer && isIdle && stripe && !product) {
      const next = router.query.next as string;
      const redirectUrl = next || PRICING_PAGE;
      window.location.href = redirectUrl;
    }
  }, [viewer, product, term, stripe, requestStatus, isCreatingSession]);

  const handleSubmit = React.useCallback(
    (e: any) => {
      e.preventDefault();

      if (requestStatus !== RequestStatus.InProgress && requestStatus !== RequestStatus.Success) {
        setRequestStatus(RequestStatus.InProgress);
        setError(null);

        auth
          .createUserWithEmailAndPassword(email, password)
          .then(async (user) => {
            setRequestStatus(RequestStatus.Success);
            setIsCreatingSession(true);
            const emailVerificationPromise = user.user?.sendEmailVerification();
            await pollForUser(client, user.user);
            const checkoutSessionPromise = maybeCreateCheckoutSession({
              product: product as string,
              term: term as string,
              stripe,
              user: user.user,
              router,
            });

            return Promise.all([emailVerificationPromise, checkoutSessionPromise]);
          })
          .catch(function (errorResponse) {
            Sentry.captureException(error);
            setRequestStatus(RequestStatus.Error);
            setError(errorResponse);
          });
      }
    },
    [email, password, requestStatus, stripe, product, term, client],
  );

  const handleGoogleSignUp = React.useCallback(async () => {
    if (!termsAccepted) {
      setRequestStatus(RequestStatus.Error);
      setError({
        statusCode: 400,
        message: 'Please accept the terms and conditions to continue',
      });
      return;
    }

    if (requestStatus !== RequestStatus.InProgress && requestStatus !== RequestStatus.Success) {
      setRequestStatus(RequestStatus.InProgress);
      setError(null);

      const provider = new firebase.auth.GoogleAuthProvider();

      try {
        const result = await auth.signInWithPopup(provider);
        setRequestStatus(RequestStatus.Success);
        setIsCreatingSession(true);

        await pollForUser(client, result.user);

        return maybeCreateCheckoutSession({
          product: product as string,
          term: term as string,
          stripe,
          user: result.user,
          router,
        });
      } catch (errorResponse: any) {
        Sentry.captureException(errorResponse);
        setRequestStatus(RequestStatus.Error);
        setError({
          statusCode: errorResponse?.code,
          message: errorResponse?.message || 'An error occurred during Google sign up',
        });
      }
    }
  }, [requestStatus, stripe, product, term, client, router, termsAccepted]);

  return (
    <>
      <Head title={TITLE} description={DESCRIPTION} useCurrentUrl />

      <div className="styled-font">
        <FormWrapper isHidden={isHidden}>
          <FormTitle>Sign Up</FormTitle>
          <div className="mb-4">
            <form onSubmit={handleSubmit}>
              <FieldWrapper>
                <Label>Email</Label>
                <Input
                  disabled={isDisabled}
                  placeholder="Enter your email address"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => {
                    if (!isDisabled) {
                      setEmail(e.target.value);
                    }
                  }}
                />
              </FieldWrapper>
              <FieldWrapper>
                <Label>Password</Label>
                <Input
                  disabled={isDisabled}
                  placeholder="Enter your password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => {
                    if (!isDisabled) {
                      setPassword(e.target.value);
                    }
                  }}
                />
              </FieldWrapper>
              <div className="my-6 flex flex-col items-center justify-center font-semibold">
                <Link href={TERMS_OF_SERVICE_PAGE} target="_blank">
                  <TextLink className="text-xs">Terms and Conditions</TextLink>
                </Link>
                <InlineCheckboxWrapperLabel>
                  <InlineCheckbox
                    type="checkbox"
                    required
                    name="terms-of-service"
                    disabled={isDisabled}
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                  />
                  <CheckboxInlineLabel>I agree to the terms and conditions</CheckboxInlineLabel>
                </InlineCheckboxWrapperLabel>
              </div>
              <Button disabled={isDisabled} type="submit" className="w-full">
                {isDisabled ? 'Creating account...' : 'Sign Up'}
              </Button>
            </form>

            <div className="relative my-6">
              <div className="relative flex justify-center text-sm">
                <span className="px-2 text-gray-500">Or continue with</span>
              </div>
            </div>

            <button
              onClick={handleGoogleSignUp}
              disabled={isDisabled}
              className="flex w-full items-center justify-center space-x-2 rounded border border-gray-600 bg-palette-black-3 px-4 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-palette-black-4 disabled:opacity-50"
            >
              <Google className="h-5 w-5" />
              <span>{isDisabled ? 'Signing up...' : 'Continue with Google'}</span>
            </button>

            {!!error && !!error.message && <ErrorWrapper>{error.message}</ErrorWrapper>}
          </div>
          <BottomLink>
            Already have an account?{' '}
            <Link
              href={`${LOGIN_PAGE}${pathname}${
                router.query.next ? `?next=${router.query.next}` : ''
              }`}
            >
              <TextLink>Log in here</TextLink>
            </Link>
          </BottomLink>
        </FormWrapper>
      </div>
    </>
  );
};

export default Signup;
